import { Button, Card, Spinner } from "@facade/components/ui";
import {
  CircleAlert,
  Download,
  FileCheck2,
  FileText,
  PackageOpen,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import type { Job } from "@facade/contracts";
import { JobProgressDetails } from "./JobProgressDetails";
import { JobResultDialog } from "./JobResultDialog";
import { isJobActive, jobQueries } from "../queries";
import { useDownloadJobResultMutation } from "../mutations";
import { useTranslation } from "react-i18next";

const TRACKED_JOB_IDS_STORAGE_KEY = "floorbit.jobs.tracked";

function readTrackedJobIds() {
  try {
    const value = JSON.parse(
      window.localStorage.getItem(TRACKED_JOB_IDS_STORAGE_KEY) ?? "[]",
    );
    return new Set<string>(
      Array.isArray(value)
        ? value.filter((jobId): jobId is string => typeof jobId === "string")
        : [],
    );
  } catch {
    return new Set<string>();
  }
}

function jobProgressTitle(job: Job, t: ReturnType<typeof useTranslation>["t"]) {
  if (job.type === "REPORT_PDF") return t("jobs.report.progressTitle");
  if (job.type === "BCF_IMPORT") return t("jobs.bcf.progressTitle");
  if (job.type === "IFC_GEOMETRY") return t("jobs.ifc.progressTitle");
  if (job.type === "IFC_FEDERATION_THUMBNAIL")
    return t("jobs.federationThumbnail.progressTitle");
  return t("jobs.generic.progressTitle");
}

function JobStatusCard({
  job,
  onDismiss,
}: {
  job: Job;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const download = useDownloadJobResultMutation();
  const active = isJobActive(job);
  const succeeded = job.status === "SUCCEEDED";
  const isReport = job.type === "REPORT_PDF";
  const isIfcGeometry = job.type === "IFC_GEOMETRY";
  const isFederationThumbnail = job.type === "IFC_FEDERATION_THUMBNAIL";
  const thumbnailFailed =
    isIfcGeometry &&
    (job.result?.thumbnail as { status?: string } | undefined)?.status ===
      "FAILED";
  const issueCount = Number(job.result?.issue_count ?? 0);
  const Icon = active
    ? isReport
      ? FileText
      : PackageOpen
    : succeeded
      ? FileCheck2
      : CircleAlert;

  const title = active
    ? jobProgressTitle(job, t)
    : succeeded
      ? isReport
        ? t("jobs.report.readyTitle")
        : isIfcGeometry
          ? t("jobs.ifc.readyTitle")
          : isFederationThumbnail
            ? t("jobs.federationThumbnail.readyTitle")
            : job.type === "BCF_IMPORT"
              ? t("jobs.bcf.readyTitle")
              : t("jobs.generic.readyTitle")
      : t("jobs.details.failedTitle");
  const description = active
    ? isFederationThumbnail
      ? t("jobs.federationThumbnail.progressDescription")
      : isIfcGeometry
        ? job.progress?.phase === "IFC_THUMBNAIL"
          ? t("jobs.ifc.thumbnailDescription")
          : t("jobs.ifc.progressDescription")
        : t("jobs.processingDescription")
    : succeeded
      ? isReport
        ? t("jobs.report.readyDescription", { count: issueCount })
        : isIfcGeometry
          ? thumbnailFailed
            ? t("jobs.ifc.readyWithPreviewWarning")
            : t("jobs.ifc.readyDescription", {
                count: Number(job.result?.processed_entities ?? 0),
              })
          : isFederationThumbnail
            ? t("jobs.federationThumbnail.readyDescription")
            : job.type === "BCF_IMPORT"
              ? t("jobs.bcf.readyDescription")
              : t("jobs.generic.readyDescription")
      : t("jobs.details.failedDescription");

  return (
    <Card size="sm" className="gap-3 bg-popover px-4 shadow-lg">
      <div className="flex items-start gap-3">
        <span
          className={
            active
              ? "grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"
              : succeeded
                ? "grid size-9 shrink-0 place-items-center rounded-full bg-emerald-500/10 text-emerald-600"
                : "grid size-9 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive"
          }
        >
          <Icon className="size-4" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{title}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {description}
          </span>
          {active ? <JobProgressDetails job={job} /> : null}
        </span>

        {active ? (
          <Spinner className="mt-2 shrink-0 text-primary" />
        ) : (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label={t("jobs.dismiss")}
            onClick={onDismiss}
          >
            <X />
          </Button>
        )}
      </div>

      {!active && succeeded && isReport ? (
        <div className="flex flex-col gap-1.5 pl-12">
          <Button
            type="button"
            className="self-start"
            disabled={download.isPending}
            onClick={() => download.mutate(job.id)}
          >
            {download.isPending ? <Spinner /> : <Download />}
            {t("jobs.report.download")}
          </Button>
          {download.isError ? (
            <span className="text-xs text-destructive">
              {t("jobs.report.downloadError")}
            </span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function JobExperience() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const jobsQuery = useQuery(jobQueries.list());
  const [trackedJobIds, setTrackedJobIds] = useState(readTrackedJobIds);
  const selectedJobId = new URLSearchParams(location.search).get("jobId");

  const visibleJobs = (jobsQuery.data ?? []).filter(
    (job) => isJobActive(job) || trackedJobIds.has(job.id),
  );

  const showJob = useCallback(
    (jobId: string | null) => {
      const params = new URLSearchParams(location.search);
      if (jobId) params.set("jobId", jobId);
      else params.delete("jobId");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    if (!jobsQuery.data) return;

    const knownJobIds = new Set(jobsQuery.data.map((job) => job.id));
    const activeJobIds = jobsQuery.data
      .filter(isJobActive)
      .map((job) => job.id);
    setTrackedJobIds((current) => {
      const next = new Set(
        [...current].filter((jobId) => knownJobIds.has(jobId)),
      );
      activeJobIds.forEach((jobId) => next.add(jobId));

      if (
        next.size === current.size &&
        [...next].every((jobId) => current.has(jobId))
      ) {
        return current;
      }
      return next;
    });
  }, [jobsQuery.data]);

  useEffect(() => {
    window.localStorage.setItem(
      TRACKED_JOB_IDS_STORAGE_KEY,
      JSON.stringify([...trackedJobIds]),
    );
  }, [trackedJobIds]);

  const dismissJob = (jobId: string) => {
    setTrackedJobIds((current) => {
      const next = new Set(current);
      next.delete(jobId);
      return next;
    });
  };

  return (
    <>
      {visibleJobs.length ? (
        <aside
          aria-label={t("jobs.statusLabel")}
          className="fixed bottom-4 right-4 z-40 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
        >
          {visibleJobs.map((job) => (
            <JobStatusCard
              key={job.id}
              job={job}
              onDismiss={() => dismissJob(job.id)}
            />
          ))}
        </aside>
      ) : null}

      <JobResultDialog jobId={selectedJobId} onClose={() => showJob(null)} />
    </>
  );
}
